import { MouseEvent, useRef, useState } from 'react';
import { Box, Popover, styled } from '@mui/material';
import { Controller, FieldValues } from 'react-hook-form';
import ColorPicker from '@rc-component/color-picker';
import '@rc-component/color-picker/assets/index.css';
import { StyledErrorText, StyledHelperTextWrapper, StyledIconWarning, StyledLabel } from 'redesign';
import { FormColorInputProps } from './form-color-input.type';

const DEFAULT_COLOR = '#000000';

const LwColorInput = <ControlFields extends FieldValues = FieldValues>({
  name,
  label,
  control,
  rules,
  disabled,
  helperText,
  'data-testid': dataTestId,
}: FormColorInputProps<ControlFields>) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const valueOnFocusRef = useRef<string>('');
  const open = Boolean(anchorEl);

  function normalizeHexInput(raw: string): string | null {
    const v = raw.trim();
    if (!v) {
      return '';
    }

    const hex = (v.startsWith('#') ? v.slice(1) : v).toLowerCase();

    if (!/^[0-9a-f]{1,6}$/.test(hex)) {
      return null;
    }

    const padded = hex.repeat(Math.ceil(6 / hex.length)).slice(0, 6);

    return `#${padded}`;
  }

  const handleSwatchClick = (event: MouseEvent<HTMLElement>) => {
    if (!disabled) {
      setAnchorEl(event.currentTarget);
    }
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState: { error } }) => {
        const hexValue = field.value ?? '';
        const displayColor = hexValue || DEFAULT_COLOR;
        const message = error ? error.message : helperText;

        const handleInputChange = (value: string) => {
          field.onChange(value);
        };

        const handleInputFocus = () => {
          valueOnFocusRef.current = field.value ?? '';
        };

        const handleInputBlur = () => {
          const normalized = normalizeHexInput(field.value ?? '');
          if (!normalized) {
            field.onChange(valueOnFocusRef.current);
            return;
          }
          field.onChange(normalized);
        };

        return (
          <Box width="100%">
            <StyledLabel required={!!rules?.required}>{label}</StyledLabel>
            <StyledContainer error={!!error} disabled={disabled}>
              <StyledSwatchButton
                type="button"
                onClick={handleSwatchClick}
                disabled={disabled}
                title={label}
                aria-label={label}
                data-testid={dataTestId}
              >
                <ColorSwatch color={displayColor} />
              </StyledSwatchButton>
              <StyledHexInput
                value={field.value ?? ''}
                onChange={(e) => handleInputChange(e.target.value)}
                onFocus={handleInputFocus}
                onBlur={handleInputBlur}
                disabled={disabled}
                placeholder="#000000"
                aria-label={`${label} hex value`}
                aria-required={!!rules?.required}
              />
              {error && (
                <Box component="span" sx={{ marginLeft: 'auto' }}>
                  <StyledIconWarning />
                </Box>
              )}
            </StyledContainer>
            <Popover
              open={open}
              anchorEl={anchorEl}
              onClose={handleClose}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
            >
              <Box p={2}>
                <ColorPicker
                  value={displayColor}
                  disabledAlpha={true}
                  onChange={(color) => {
                    field.onChange(color.toHexString());
                  }}
                />
              </Box>
            </Popover>
            <StyledHelperTextWrapper className="error-text-wrapper">
              {message && (
                <StyledErrorText className={error ? 'error-state' : ''}>{message}</StyledErrorText>
              )}
            </StyledHelperTextWrapper>
          </Box>
        );
      }}
    />
  );
};

const ColorSwatch = styled(Box)<{ color: string }>(({ theme, color }) => ({
  width: 24,
  height: 24,
  borderRadius: theme.spacing(2),
  backgroundColor: color,
  border: `2px solid ${theme.palette.lwPrimaryAccentDark[40]}`,
  flexShrink: 0,
}));

const StyledContainer = styled(Box)<{ error?: boolean; disabled?: boolean }>(
  ({ theme, error, disabled }) => ({
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    width: '100%',
    minHeight: 56,
    padding: theme.spacing(2, 4),
    border: `2px solid ${error ? theme.palette.lwDanger[100] : theme.palette.lwPrimaryAccentDark[40]}`,
    borderRadius: theme.spacing(3),
    backgroundColor: theme.palette.common.white,
    opacity: disabled ? 0.5 : 1,
    '&:hover': disabled
      ? {}
      : {
          borderColor: theme.palette.lwPrimaryAccentLight[100],
          boxShadow: `0 0 0 4px ${theme.palette.lwPrimaryAccentLight[10]}`,
        },
    '&:focus-within': disabled
      ? {}
      : {
          outline: 'none',
          borderColor: theme.palette.lwPrimaryAccentLight[100],
          boxShadow: `0 0 0 4px ${theme.palette.lwPrimaryAccentLight[10]}`,
        },
  })
);

const StyledSwatchButton = styled('button')<{ disabled?: boolean }>(({ disabled }) => ({
  padding: 0,
  border: 'none',
  background: 'none',
  cursor: disabled ? 'not-allowed' : 'pointer',
  display: 'flex',
  alignItems: 'center',
}));

const StyledHexInput = styled('input')(({ theme }) => ({
  flex: 1,
  minWidth: 0,
  border: 'none',
  background: 'none',
  fontFamily: 'Inter',
  fontSize: 14,
  fontWeight: 500,
  color: theme.palette.text.primary,
  '&::placeholder': {
    color: theme.palette.text.secondary,
  },
  '&:focus': {
    outline: 'none',
  },
}));

export { LwColorInput };
